/**
 * Nutrición: registrar lo que se come. Lógica compartida por las dos variantes.
 *
 * El flujo por defecto es **escribir en lenguaje natural** ("dos huevos revueltos y una tostada
 * con palta"), porque es lo más rápido y es lo que la gente hace igual. La IA estima, el usuario
 * corrige lo que esté mal, y sólo entonces se guarda.
 *
 * §10 de la spec prohíbe prescribir dietas o números de calorías: acá no hay objetivos calculados
 * ni recomendaciones. Si hay un objetivo, lo escribió el usuario en Ajustes.
 *
 * Hora y momento del día son una SUGERENCIA del reloj hasta que el usuario los toca. Ver
 * `horaEnZona` y el efecto del reloj más abajo: es lo que evita que el formulario ofrezca "Cena"
 * a las nueve de la mañana y que esa comida quede anotada donde no va.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MOMENTOS,
  NOMBRE_MOMENTO,
  UMBRAL_CONFIANZA_COMIDA,
  addDias,
  constancia,
  cumplimiento,
  diasDesdeUltimoRegistro,
  esFechaValida,
  fechaLarga,
  kcalDeMacros,
  momentoPorHora,
  promedioNutricion,
  proteinaPorKg,
  totalesDelDia,
  totalesPorDia,
  validarAlimento,
  type Alimento,
  type AlimentoEstimado,
  type Fecha,
  type Hallazgo,
  type MomentoDelDia,
  type Resultado,
} from '@mg/core';

import { ErrorAPI, pedir } from '../datos/api.ts';
import { guardarLocal } from '../datos/db.ts';
import { borrarAlimento, editarAlimento, guardarAlimentos } from './acciones.ts';
import { useDatos } from './useDatos.ts';

interface RespuestaEstimacion {
  lectura: { confianza_global: number; alimentos: AlimentoEstimado[]; no_estimables: string[] };
  jsonCrudo: string;
  borrador: { momento: MomentoDelDia; alimentos: AlimentoEstimado[]; paraRevisar: number[]; noEstimables: string[] };
  validaciones: Resultado[];
  modelo: string;
}

export type PasoNutricion = 'escribir' | 'estimando' | 'revisar' | 'guardado';

/** Un fallo con su salida: `sinRed` es lo que habilita el "cargalo a mano" en las dos variantes. */
export interface ErrorNutricion {
  mensaje: string;
  sinRed: boolean;
}

/** Cuánto queda abierta la vuelta atrás de un borrado antes de que el aviso se vaya solo. */
const SEGUNDOS_DESHACER = 10;

/** Cada cuánto se vuelve a mirar el reloj con el formulario abierto y sin tocar. */
const MS_RELOJ = 60_000;

/** La zona del perfil es la que manda; sólo se usa este default si todavía no cargó. */
const ZONA_POR_DEFECTO = 'America/Santiago';

/**
 * La hora del reloj **en la zona del perfil**, no en la del aparato.
 *
 * `datos.hoy` sale de `hoyEnZona(perfil.zona_horaria)`, así que leer la hora de
 * `new Date().toTimeString()` mezclaba dos relojes distintos: el día era el de Chile y el momento
 * el del dispositivo. Con el teléfono en otra zona —o el navegador en UTC— la misma comida quedaba
 * anotada en el día correcto y en el momento equivocado, y la app se contradecía en la misma
 * frase: "Guardado en la Cena de hoy" a las 09:03.
 *
 * Es primo hermano de `hoyEnZona` y su lugar natural es `packages/core/src/dates.ts`; vive acá
 * hasta que alguien lo mude, porque core no es parte de este cambio.
 */
const horaEnZona = (zona: string, ahora: Date = new Date()): string => {
  try {
    // 'h23' y no `hour12: false`: sin eso, algunos motores devuelven "24:07" a la medianoche.
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: zona,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(ahora);
  } catch {
    // La zona se escribe a mano en Ajustes: si es inválida, el reloj del aparato es mejor que nada.
    return ahora.toTimeString().slice(0, 5);
  }
};

/**
 * El `<input type="time">` devuelve '' cuando se borra el campo, y `Number('')` es 0 —o sea,
 * finito—, así que un campo vacío se colaba como las 00:00 y sugería "Desayuno".
 */
const esHoraValida = (h: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(h);

/**
 * El momento que sugiere una hora, o null si la hora no se puede leer.
 *
 * Los cortes son los de `momentoPorHora` (@mg/core), que es también el fallback del servidor al
 * estimar: desayuno hasta las 11, almuerzo hasta las 16, cena hasta las 22 y snack de ahí hasta la
 * mañana. Un solo lugar para el corte, o el cliente y el servidor archivan la misma comida en
 * momentos distintos. Y es una sugerencia: alcanza un toque para cambiarla, y desde ahí manda el
 * usuario.
 */
const momentoDeLaHora = (hora: string): MomentoDelDia | null =>
  esHoraValida(hora) ? momentoPorHora(hora) : null;

/** "la Cena de hoy", "el Desayuno de ayer": sin el artículo la frase de confirmación no se lee. */
const ARTICULO: Record<MomentoDelDia, string> = {
  desayuno: 'el',
  almuerzo: 'el',
  cena: 'la',
  snack: 'el',
};

/**
 * Traduce un fallo de estimación a mensaje + salida. El texto genérico de la API —"Sin conexión.
 * Lo que registres queda guardado y sube después"— es FALSO acá: estimar no guarda nada, necesita
 * el modelo del servidor. Sin red la verdad es que no hay estimación, y la salida es cargarlo a
 * mano.
 */
const falloAlEstimar = (e: unknown, accion: string, porDefecto: string): ErrorNutricion =>
  e instanceof ErrorAPI && e.codigo === 'sin_red'
    ? { mensaje: `${accion} necesita conexión. Cargalo a mano y queda guardado igual.`, sinRed: true }
    : { mensaje: e instanceof ErrorAPI ? e.message : porDefecto, sinRed: false };

/** Un alimento en el borrador, con los campos como texto para poder editarlos. */
export interface FilaBorrador {
  descripcion: string;
  kcal: string;
  proteina_g: string;
  carbohidratos_g: string;
  grasa_g: string;
  fibra_g: string;
  confianza: number | null;
  revisar: boolean;
}

const num = (s: string): number | null => {
  const t = s.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};
const s = (v: number | null | undefined): string => (v === null || v === undefined ? '' : String(v));

const filaVacia = (): FilaBorrador => ({
  descripcion: '',
  kcal: '',
  proteina_g: '',
  carbohidratos_g: '',
  grasa_g: '',
  fibra_g: '',
  confianza: null,
  revisar: false,
});

export function useNutricion() {
  const datos = useDatos();

  /** La misma zona con la que se calcula `datos.hoy`: día, hora y momento tienen que ir juntos. */
  const zona = datos.perfil?.zona_horaria || ZONA_POR_DEFECTO;

  const [dia, fijarDia] = useState<Fecha>(datos.hoy);
  const [paso, setPaso] = useState<PasoNutricion>('escribir');
  const [texto, setTexto] = useState('');
  const [momento, fijarMomento] = useState<MomentoDelDia>(() => momentoPorHora(horaEnZona(ZONA_POR_DEFECTO)));
  const [hora, fijarHora] = useState(() => horaEnZona(ZONA_POR_DEFECTO));
  /**
   * Qué tocó el usuario a mano. Mientras estén en `false`, hora y momento son una sugerencia y el
   * reloj los sigue moviendo; apenas los toca, la app deja de pisárselos.
   */
  const [horaAMano, setHoraAMano] = useState(false);
  const [momentoAMano, setMomentoAMano] = useState(false);
  const [filas, setFilas] = useState<FilaBorrador[]>([]);
  const [noEstimables, setNoEstimables] = useState<string[]>([]);
  const [jsonCrudo, setJsonCrudo] = useState<string | null>(null);
  const [modelo, setModelo] = useState<string | null>(null);
  const [error, setError] = useState<ErrorNutricion | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [vistaPrevia, setVistaPrevia] = useState<string | null>(null);

  /**
   * El input de fecha devuelve '' cuando se borra el campo, y una fecha inválida hace explotar
   * toda la aritmética de calendario. Sin día no hay nada que mirar: se vuelve a hoy.
   */
  const setDia = useCallback(
    (f: string) => fijarDia(esFechaValida(f) ? f : datos.hoy),
    [datos.hoy],
  );

  // --- El reloj: cuándo se come ------------------------------------------

  /**
   * El reloj sigue corriendo con la pantalla abierta, y esta app es una PWA que queda abierta
   * días: calcular la hora una sola vez en el montaje dejaba el formulario ofreciendo el momento
   * de anoche a la mañana siguiente —"Cena" a las 09:03— y esa sugerencia se guarda tal cual si
   * nadie la mira. Se vuelve a mirar el reloj cada minuto y al volver al frente (que es el caso
   * real: el teléfono estuvo en el bolsillo doce horas), sólo mientras la sugerencia siga siendo
   * una sugerencia.
   *
   * Con la hora puesta a mano el reloj no manda más: ahí el momento lo reacomoda `setHora`.
   * En `revisar` también se para, porque el usuario ya está confirmando un registro concreto y la
   * hora no puede moverse bajo el cartel que dice dónde va a quedar.
   */
  useEffect(() => {
    if (paso !== 'escribir' || horaAMano) return;

    const mirarElReloj = (): void => {
      const h = horaEnZona(zona);
      fijarHora(h);
      if (!momentoAMano) fijarMomento(momentoPorHora(h));
    };

    mirarElReloj();
    const id = window.setInterval(mirarElReloj, MS_RELOJ);
    document.addEventListener('visibilitychange', mirarElReloj);
    window.addEventListener('focus', mirarElReloj);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', mirarElReloj);
      window.removeEventListener('focus', mirarElReloj);
    };
  }, [paso, zona, horaAMano, momentoAMano]);

  /** Elegir el momento a mano lo fija: ni el reloj ni un cambio de hora lo vuelven a mover. */
  const setMomento = useCallback((m: MomentoDelDia) => {
    setMomentoAMano(true);
    fijarMomento(m);
  }, []);

  /**
   * Cambiar la hora mueve el momento SUGERIDO —anotar a las 21:00 no es un desayuno—, pero nunca
   * pisa el que el usuario ya eligió a mano: si puso "Cena" y después corrige la hora a las 09:00
   * porque está anotando la cena de anoche, sigue siendo la cena.
   */
  const setHora = useCallback(
    (h: string) => {
      setHoraAMano(true);
      fijarHora(h);
      if (momentoAMano) return;
      const m = momentoDeLaHora(h);
      if (m) fijarMomento(m);
    },
    [momentoAMano],
  );

  /**
   * La hora que se va a guardar de verdad. El campo `type="time"` se puede vaciar, y un registro
   * sin hora desordena el día entero (la lista se ordena por hora): un campo vacío vuelve al reloj.
   */
  const horaAGuardar = useMemo(() => (esHoraValida(hora) ? hora : horaEnZona(zona)), [hora, zona]);

  const objetivos = useMemo(
    () => ({
      proteina_g: datos.perfil?.objetivo_proteina_g ?? null,
      kcal: datos.perfil?.objetivo_kcal ?? null,
    }),
    [datos.perfil],
  );

  // --- Lo ya registrado ---------------------------------------------------

  const delDia = useMemo(
    () => datos.comidas.filter((c) => c.fecha === dia).sort((a, b) => (a.hora < b.hora ? -1 : 1)),
    [datos.comidas, dia],
  );

  const totales = useMemo(() => totalesDelDia(datos.comidas, dia), [datos.comidas, dia]);

  const promedio7 = useMemo(() => promedioNutricion(datos.comidas, datos.hoy, 7), [datos.comidas, datos.hoy]);

  const proteina = useMemo(
    () => proteinaPorKg(datos.comidas, datos.medidas, datos.hoy, 7),
    [datos.comidas, datos.medidas, datos.hoy],
  );

  const historial = useMemo(() => {
    const desde = totalesPorDia(datos.comidas);
    return desde.slice(-30);
  }, [datos.comidas]);

  const porDia = useMemo(
    () => cumplimiento(datos.comidas, objetivos, datos.hoy, 14),
    [datos.comidas, objetivos, datos.hoy],
  );

  const constanciaRegistro = useMemo(
    () => constancia(datos.comidas, datos.hoy, 14),
    [datos.comidas, datos.hoy],
  );

  const diasSinAnotar = useMemo(
    () => diasDesdeUltimoRegistro(datos.comidas, datos.hoy),
    [datos.comidas, datos.hoy],
  );

  // --- Cómo se dice el día ------------------------------------------------

  /** 'hoy' · 'ayer' · 'jueves 14 de agosto'. Sin artículo: se usa detrás de un "·". */
  const etiquetaDia = useMemo(() => {
    if (dia === datos.hoy) return 'hoy';
    if (dia === addDias(datos.hoy, -1)) return 'ayer';
    return fechaLarga(dia);
  }, [dia, datos.hoy]);

  /**
   * Dónde va a quedar lo que se guarde: "la Cena de hoy", "el Desayuno del jueves 14 de agosto".
   * Confirmar sin ver esto es confirmar a ciegas.
   */
  const dondeQueda = useMemo(() => {
    const comida = `${ARTICULO[momento]} ${NOMBRE_MOMENTO[momento]}`;
    const cuando = etiquetaDia === 'hoy' || etiquetaDia === 'ayer' ? `de ${etiquetaDia}` : `del ${etiquetaDia}`;
    return `${comida} ${cuando}`;
  }, [momento, etiquetaDia]);

  // --- Estimar ------------------------------------------------------------

  const cargarBorrador = useCallback((r: RespuestaEstimacion) => {
    const revisar = new Set(r.borrador.paraRevisar);
    setFilas(
      r.borrador.alimentos.map((a, i) => ({
        descripcion: a.descripcion,
        kcal: s(a.kcal),
        proteina_g: s(a.proteina_g),
        carbohidratos_g: s(a.carbohidratos_g),
        grasa_g: s(a.grasa_g),
        fibra_g: s(a.fibra_g),
        confianza: a.confianza,
        revisar: revisar.has(i),
      })),
    );
    setNoEstimables(r.borrador.noEstimables);
    // `fijarMomento` y no `setMomento`: el servidor devuelve el momento que le mandamos, así que
    // esto no es una elección del usuario y no puede marcarse como tal.
    fijarMomento(r.borrador.momento);
    setJsonCrudo(r.jsonCrudo);
    setModelo(r.modelo);
    setPaso('revisar');
  }, []);

  const estimarTexto = useCallback(async (): Promise<void> => {
    if (!texto.trim()) return;
    setPaso('estimando');
    setError(null);
    setVistaPrevia(null);
    try {
      const r = await pedir<RespuestaEstimacion>('/nutricion/estimar', {
        metodo: 'POST',
        timeoutMs: 45_000,
        cuerpo: { texto, momento, hora: horaAGuardar },
      });
      cargarBorrador(r);
    } catch (e) {
      setError(falloAlEstimar(e, 'Estimar', 'No se pudo estimar.'));
      setPaso('escribir');
    }
  }, [texto, momento, horaAGuardar, cargarBorrador]);

  const estimarFoto = useCallback(
    async (archivo: File): Promise<void> => {
      setPaso('estimando');
      setError(null);
      try {
        const imagen = await comprimir(archivo);
        setVistaPrevia(imagen);
        const r = await pedir<RespuestaEstimacion>('/nutricion/estimar', {
          metodo: 'POST',
          timeoutMs: 60_000,
          cuerpo: { imagen, texto: texto.trim() || undefined, momento, hora: horaAGuardar },
        });
        cargarBorrador(r);
      } catch (e) {
        setError(falloAlEstimar(e, 'Leer la foto', 'No se pudo leer la foto.'));
        setPaso('escribir');
      }
    },
    [texto, momento, horaAGuardar, cargarBorrador],
  );

  /** Cargar a mano, sin IA. Es el camino de escape cuando no hay señal o la estimación no sirve. */
  const empezarAMano = useCallback(() => {
    setFilas([filaVacia()]);
    setNoEstimables([]);
    setJsonCrudo(null);
    setModelo(null);
    setError(null);
    setPaso('revisar');
  }, []);

  // --- Editar el borrador -------------------------------------------------

  const cambiarFila = useCallback((i: number, cambios: Partial<FilaBorrador>) => {
    setFilas((prev) => prev.map((f, j) => (j === i ? { ...f, ...cambios } : f)));
  }, []);

  const agregarFila = useCallback(() => setFilas((prev) => [...prev, filaVacia()]), []);

  const quitarFila = useCallback((i: number) => {
    setFilas((prev) => prev.filter((_, j) => j !== i));
  }, []);

  const validaciones = useMemo<Resultado[]>(
    () =>
      filas.map((f) =>
        validarAlimento({
          descripcion: f.descripcion,
          kcal: num(f.kcal),
          proteina_g: num(f.proteina_g),
          carbohidratos_g: num(f.carbohidratos_g),
          grasa_g: num(f.grasa_g),
        }),
      ),
    [filas],
  );

  const errores = useMemo<Hallazgo[]>(
    () => validaciones.flatMap((v) => v.hallazgos.filter((h) => h.severidad === 'error')),
    [validaciones],
  );

  const aConfirmar = useMemo<Hallazgo[]>(
    () => validaciones.flatMap((v) => v.hallazgos.filter((h) => h.severidad === 'confirmar')),
    [validaciones],
  );

  const [confirmado, setConfirmado] = useState(false);

  const totalesBorrador = useMemo(
    () =>
      filas.reduce(
        (acc, f) => ({ kcal: acc.kcal + (num(f.kcal) ?? 0), proteina_g: acc.proteina_g + (num(f.proteina_g) ?? 0) }),
        { kcal: 0, proteina_g: 0 },
      ),
    [filas],
  );

  const puedeGuardar = filas.length > 0 && errores.length === 0 && (aConfirmar.length === 0 || confirmado);

  const confirmar = useCallback(async (): Promise<boolean> => {
    if (!datos.perfil || !puedeGuardar) return false;
    setGuardando(true);
    setError(null);
    try {
      await guardarAlimentos(
        datos.perfil.id,
        { fecha: dia, hora: horaAGuardar, momento, fuente: jsonCrudo ? (vistaPrevia ? 'ia_foto' : 'ia_texto') : 'manual' },
        filas.map((f) => ({
          descripcion: f.descripcion.trim(),
          kcal: num(f.kcal)!,
          proteina_g: num(f.proteina_g)!,
          carbohidratos_g: num(f.carbohidratos_g),
          grasa_g: num(f.grasa_g),
          fibra_g: num(f.fibra_g),
          confianza: f.confianza,
        })),
      );
      setPaso('guardado');
      return true;
    } catch (e) {
      setError({ mensaje: e instanceof Error ? e.message : 'No se pudo guardar.', sinRed: false });
      return false;
    } finally {
      setGuardando(false);
    }
  }, [datos.perfil, puedeGuardar, dia, horaAGuardar, momento, filas, jsonCrudo, vistaPrevia]);

  // --- Borrar, con vuelta atrás -------------------------------------------

  const [borrado, setBorrado] = useState<Alimento | null>(null);
  const relojDeshacer = useRef<number | null>(null);

  // El timeout sobrevive a salir de la pantalla si no se limpia, y dispara sobre un componente
  // que ya no está.
  useEffect(
    () => () => {
      if (relojDeshacer.current !== null) clearTimeout(relojDeshacer.current);
    },
    [],
  );

  /**
   * Borra y deja abierta la vuelta atrás unos segundos. El borrado es local: si la fila ya se
   * había subido, el próximo pull la trae de nuevo (deuda conocida, `docs/flujo.md` §7). La UI
   * lo dice en vez de fingir que el borrado local alcanza.
   */
  const borrarConDeshacer = useCallback(async (alimento: Alimento): Promise<void> => {
    await borrarAlimento(alimento);
    if (relojDeshacer.current !== null) clearTimeout(relojDeshacer.current);
    setBorrado(alimento);
    relojDeshacer.current = window.setTimeout(() => {
      relojDeshacer.current = null;
      setBorrado(null);
    }, SEGUNDOS_DESHACER * 1000);
  }, []);

  const olvidarBorrado = useCallback(() => {
    if (relojDeshacer.current !== null) clearTimeout(relojDeshacer.current);
    relojDeshacer.current = null;
    setBorrado(null);
  }, []);

  const deshacerBorrado = useCallback(async (): Promise<void> => {
    if (!borrado) return;
    // Vuelve con su id y su idem_key: si ya estaba en la planilla, el push la reconoce y no
    // aparece dos veces.
    await guardarLocal('comidas', [borrado]);
    olvidarBorrado();
  }, [borrado, olvidarBorrado]);

  const reiniciar = useCallback(() => {
    setPaso('escribir');
    setTexto('');
    setFilas([]);
    setNoEstimables([]);
    setJsonCrudo(null);
    setModelo(null);
    setVistaPrevia(null);
    setConfirmado(false);
    setError(null);
    // Formulario nuevo: hora y momento vuelven a ser una sugerencia del reloj. Lo que el usuario
    // eligió a mano valía para la comida que acaba de guardar, no para la próxima.
    const h = horaEnZona(zona);
    setHoraAMano(false);
    setMomentoAMano(false);
    fijarHora(h);
    fijarMomento(momentoPorHora(h));
  }, [zona]);

  return {
    datos,
    dia,
    setDia,
    etiquetaDia,
    dondeQueda,
    objetivos,
    delDia,
    totales,
    promedio7,
    proteina,
    historial,
    porDia,
    constanciaRegistro,
    diasSinAnotar,

    paso,
    texto,
    setTexto,
    momento,
    setMomento,
    hora,
    setHora,
    /** La hora que realmente se va a guardar: nunca vacía. Es la que la UI tiene que mostrar. */
    horaAGuardar,
    /** `true` mientras el momento lo pone el reloj. En `false` lo eligió el usuario y no se toca. */
    momentoEsSugerencia: !momentoAMano,
    filas,
    cambiarFila,
    agregarFila,
    quitarFila,
    noEstimables,
    jsonCrudo,
    modelo,
    vistaPrevia,
    validaciones,
    errores,
    aConfirmar,
    confirmado,
    setConfirmado,
    totalesBorrador,
    puedeGuardar,
    guardando,
    error,

    estimarTexto,
    estimarFoto,
    empezarAMano,
    confirmar,
    reiniciar,

    editar: editarAlimento,
    borrado,
    borrarConDeshacer,
    deshacerBorrado,
    olvidarBorrado,

    momentos: MOMENTOS,
    nombreMomento: NOMBRE_MOMENTO,
    umbralConfianza: UMBRAL_CONFIANZA_COMIDA,
    kcalDeMacros,
  };
}

/** Reduce la foto antes de subirla: 12 MP no mejoran la estimación y tardan una eternidad. */
function comprimir(archivo: File): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onerror = () => rechazar(new Error('No se pudo leer la imagen.'));
    lector.onload = () => {
      const dataUri = String(lector.result);
      const img = new Image();
      img.onerror = () => resolver(dataUri);
      img.onload = () => {
        const max = 1280;
        const escala = Math.min(1, max / Math.max(img.width, img.height));
        if (escala >= 1) return resolver(dataUri);
        const lienzo = document.createElement('canvas');
        lienzo.width = Math.round(img.width * escala);
        lienzo.height = Math.round(img.height * escala);
        const ctx = lienzo.getContext('2d');
        if (!ctx) return resolver(dataUri);
        ctx.drawImage(img, 0, 0, lienzo.width, lienzo.height);
        resolver(lienzo.toDataURL('image/jpeg', 0.85));
      };
      img.src = dataUri;
    };
    lector.readAsDataURL(archivo);
  });
}
